import { useEffect, useState } from 'react'
import DuplicateImportModal from '../DuplicateImportModal.jsx'
import { getAuthHeaders } from '../utils/auth.js'
import { Button, Input } from '../design-system/primitives/index.js'
import { StatusPill } from '../design-system/patterns/index.js'
import { statusMessageText, statusMessageVariant } from './admin/documents/documentsAdminUtils.js'

export default function DriveImport({ embedded = false }) {
  const [connected, setConnected] = useState(false)
  const [authUrl, setAuthUrl] = useState('')
  const [showOAuthUrl, setShowOAuthUrl] = useState(false)
  const [q, setQ] = useState('')
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState('')
  const [importing, setImporting] = useState({})
  const [hasMore, setHasMore] = useState(false)
  const [pageSize] = useState(20)
  const [showAll, setShowAll] = useState(false)
  const [initialLoadCount] = useState(5)
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [duplicateFileInfo, setDuplicateFileInfo] = useState(null)
  const [pendingImport, setPendingImport] = useState(null) // { id, fileName }

  async function startOAuth() {
    try {
      setStatus('')
      const res = await fetch('/drive/oauth/start')
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`)
      setAuthUrl(data.auth_url)
      setConnected(false)
      
      // Try to open in new tab, but handle popup blocking
      const newWindow = window.open(data.auth_url, '_blank')
      
      if (newWindow) {
        setStatus('🔗 Authorization window opened. Please complete the authorization in the new tab, then click "Refresh" below.')
        setShowOAuthUrl(false)
        
        // Auto-verify after a longer delay to give user time to complete auth
        setTimeout(() => {
          verifyConnected()
        }, 5000)
      } else {
        // Popup was blocked, show manual instructions
        setStatus('🔗 Popup blocked! Please copy the URL below and open it manually in a new tab:')
        setShowOAuthUrl(true)
      }
    } catch (e) {
      setStatus('❌ Error: ' + (e.message || e))
    }
  }

  async function verifyConnected() {
    try {
      setStatus('🔄 Checking Google Drive connection...')
      setShowOAuthUrl(false) // Hide OAuth URL when verifying connection
      const params = new URLSearchParams({ folder_name: 'Web Agent Files' })
      const res = await fetch(`/drive/files?${params}`, {
        headers: getAuthHeaders()
      })
      const data = await res.json()
      if (res.ok) {
        setConnected(true)
        setStatus('✅ Connected to Google Drive - showing files from "Web Agent Files" folder')
        setFiles(data.files || [])
        setShowOAuthUrl(false) // Ensure URL is hidden when connected
      } else {
        setConnected(false)
        // 400/404 errors are expected if folder doesn't exist or user isn't authorized
        // Don't show error in console for expected cases
        if (res.status === 400 || res.status === 404) {
          if (data.detail && data.detail.includes('not authorized')) {
            setStatus('🔑 Not authorized. Click "Connect Drive" to grant access to your Google Drive files.')
          } else {
            setStatus('🔑 Please connect to Google Drive to access files.')
          }
        } else if (data.detail && data.detail.includes('not authorized')) {
          setStatus('🔑 Not authorized. Click "Connect Drive" to grant access to your Google Drive files.')
        } else {
          setStatus('❌ Connection failed: ' + (data.detail || 'Unknown error'))
        }
      }
    } catch (e) {
      setConnected(false)
      // Only log unexpected errors (not network errors which are expected)
      if (e.name !== 'TypeError' || !e.message.includes('fetch')) {
        console.error('Drive connection error:', e)
      }
      setStatus('❌ Connection failed: ' + (e.message || 'Unknown error'))
    }
  }

  async function searchDrive(loadMore = false) {
    if (!connected) {
      setStatus('⚠️ Please connect to Google Drive first')
      return
    }
    
    setLoading(true)
    if (!loadMore) {
      setStatus('')
      setFiles([])
      setShowAll(false) // Reset show all when searching
    }
    
    try {
      setStatus('🔍 Searching Google Drive...')
      const currentFiles = loadMore ? files : []
      // Build URL with folder filter for "Web Agent Files"
      const params = new URLSearchParams({
        page_size: pageSize,
        folder_name: 'Web Agent Files'
      })
      if (q) {
        params.set('q', q)
      }
      const res = await fetch(`/drive/files?${params}`, {
        headers: getAuthHeaders()
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`)
      
      const newFiles = data.files || []
      setFiles(loadMore ? [...currentFiles, ...newFiles] : newFiles)
      setHasMore(data.hasMore || false)
      
      if (!newFiles.length && !loadMore) {
        setStatus(q ? `No files found for "${q}"` : 'No files found in Google Drive')
      } else {
        const totalFiles = loadMore ? currentFiles.length + newFiles.length : newFiles.length
        setStatus(`Found ${totalFiles} file${totalFiles !== 1 ? 's' : ''}${data.hasMore ? ' (more available)' : ''}`)
      }
    } catch (e) {
      setStatus('❌ Search error: ' + (e.message || e))
    } finally {
      setLoading(false)
    }
  }

  async function importToRag(id, fileName, force = false) {
    setImporting(prev => ({ ...prev, [id]: true }))
    setStatus(`📥 Importing "${fileName}"...`)
    
    try {
      const url = `/rag/import_drive?file_id=${encodeURIComponent(id)}${force ? '&force=true' : ''}`
      const res = await fetch(url, { 
        method: 'POST',
        headers: getAuthHeaders()
      })
      
      // Check if response is ok before trying to parse JSON
      if (!res.ok) {
        const errorText = await res.text()
        console.error('Import failed:', res.status, errorText)
        throw new Error(`HTTP ${res.status}: ${errorText}`)
      }
      
      // Check if response has content before parsing JSON
      const responseText = await res.text()
      if (!responseText.trim()) {
        console.error('Empty response from server')
        throw new Error('Server returned empty response')
      }
      
      let data
      try {
        data = JSON.parse(responseText)
      } catch (parseError) {
        console.error('JSON parse error:', parseError, 'Response:', responseText)
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}...`)
      }
      
      // Check if this is a duplicate response
      if (data.duplicate === true) {
        setImporting(prev => ({ ...prev, [id]: false }))
        setDuplicateFileInfo({
          fileName: fileName,
          existingFile: data.file
        })
        setPendingImport({ id, fileName })
        setShowDuplicateModal(true)
        return
      }
      
      setStatus(`✅ Imported "${data.file?.name || fileName}" — ${data.chunks} chunks added`)
      
      // Remove the imported file from the search results
      setFiles(prev => prev.filter(file => file.id !== id))
      
    } catch (e) {
      setStatus('❌ Import error: ' + (e.message || e))
    } finally {
      setImporting(prev => ({ ...prev, [id]: false }))
    }
  }

  function handleDuplicateConfirm() {
    if (pendingImport) {
      setShowDuplicateModal(false)
      // Import with force=true
      importToRag(pendingImport.id, pendingImport.fileName, true)
      setPendingImport(null)
      setDuplicateFileInfo(null)
    }
  }

  function handleDuplicateCancel() {
    setShowDuplicateModal(false)
    setPendingImport(null)
    setDuplicateFileInfo(null)
    setStatus('Import cancelled')
  }

  // Auto-connect on component mount
  useEffect(() => { 
    verifyConnected() 
  }, [])

  // Check for OAuth callback in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const authStatus = urlParams.get('drive_auth')
    const message = urlParams.get('message')
    
    if (authStatus === 'success') {
      setStatus('✅ Authorization successful! Refreshing connection...')
      setShowOAuthUrl(false) // Hide OAuth URL on successful auth
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)
      // Verify connection
      setTimeout(() => {
        verifyConnected()
      }, 1000)
    } else if (authStatus === 'error') {
      setStatus('❌ Authorization failed: ' + (message || 'Unknown error'))
      setShowOAuthUrl(false) // Hide OAuth URL on error
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [])

  // Auto-import function that handles connection automatically
  async function _autoImportFromDrive(fileId, fileName) {
    try {
      // First check if we're connected
      if (!connected) {
        setStatus('🔗 Connecting to Google Drive...')
        await verifyConnected()
        if (!connected) {
          setStatus('❌ Please connect to Google Drive first')
          return
        }
      }
      
      // Now import the file
      await importToRag(fileId, fileName)
    } catch (e) {
      setStatus('❌ Auto-import failed: ' + (e.message || e))
    }
  }

  const statusVariant = statusMessageVariant(status)
  const wrapperClass = embedded ? 'drive-import drive-import--embedded' : 'drive-import drive-import--legacy-card'

  return (
    <div className={wrapperClass}>
      {!embedded && (
        <div className="card-title">Import from Google Drive</div>
      )}
      <p className="admin-panel-description">
        {connected
          ? 'Showing files from "Web Agent Files" folder'
          : 'Connect your Google Drive to search and import documents from "Web Agent Files" folder'}
      </p>

      {!connected && (
        <div className="drive-import-auth-notice" role="status">
          <strong>Authorization required:</strong> Click &quot;Connect Drive&quot; to grant access to your Google Drive files
        </div>
      )}

      <div className="admin-action-row drive-import-actions">
        <Button
          variant={connected ? 'secondary' : 'primary'}
          onClick={startOAuth}
          disabled={connected}
          aria-label={connected ? 'Already connected to Google Drive' : 'Connect to Google Drive'}
        >
          {connected ? 'Connected' : 'Connect Drive'}
        </Button>
        <Button
          onClick={verifyConnected}
          variant="secondary"
          aria-label="Refresh Google Drive connection status"
        >
          Refresh
        </Button>
      </div>

      {showOAuthUrl && authUrl && (
        <button
          type="button"
          className="drive-import-oauth-url"
          title="Click to copy URL"
          onClick={() => {
            navigator.clipboard.writeText(authUrl).then(() => {
              setStatus('✅ URL copied to clipboard! Open it in a new tab.')
            }).catch(() => {
              setStatus('URL ready to copy. Open it in a new tab.')
            })
          }}
        >
          {authUrl}
        </button>
      )}

      <div className="admin-action-row drive-import-search">
        <label htmlFor="drive-search" className="sr-only">Search Google Drive files</label>
        <Input
          id="drive-search"
          placeholder="Search by name, type (pdf, doc, image), or content..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && searchDrive()}
          aria-describedby="search-help"
        />
        <Button
          onClick={searchDrive}
          disabled={loading || !connected}
          variant="primary"
          className={loading ? 'loading' : ''}
          aria-label={loading ? 'Searching Google Drive' : 'Search Google Drive files'}
        >
          {loading ? 'Searching…' : 'Search'}
        </Button>
      </div>

      {!q && connected && (
        <p id="search-help" className="admin-meta-caption drive-import-search-help">
          Try: &quot;pdf&quot;, &quot;doc&quot;, &quot;image&quot;, &quot;recent&quot;, or any file name
        </p>
      )}

      {status && statusVariant && (
        <StatusPill
          variant={statusVariant}
          className="status-pill--block doc-status-pill"
          onDismiss={statusVariant === 'error' ? () => setStatus('') : undefined}
        >
          {statusMessageText(status)}
        </StatusPill>
      )}

      {!!files.length && (
        <div className="drive-import-results">
          <p className="admin-meta-caption drive-import-results-count">
            Found {files.length} file{files.length !== 1 ? 's' : ''}
            {q && ` for "${q}"`}
            {files.length > initialLoadCount && !showAll && (
              <span className="drive-import-results-hint">
                {' '}(showing {initialLoadCount} most recent)
              </span>
            )}
          </p>
          <ul className="drive-import-file-list">
            {(showAll ? files : files.slice(0, initialLoadCount)).map((f) => (
              <li key={f.id} className="doc-item drive-import-file-item">
                <i className="fas fa-file doc-item-icon doc-icon-other" aria-hidden="true" />
                <div className="doc-info">
                  <div className="doc-name" title={f.name}>{f.name}</div>
                  <div className="doc-meta">
                    {f.type}
                    {f.sizeFormatted ? ` · ${f.sizeFormatted}` : ''}
                    {f.modifiedFormatted ? ` · ${f.modifiedFormatted}` : ''}
                  </div>
                  {f.description && (
                    <div className="doc-meta drive-import-file-desc">{f.description}</div>
                  )}
                </div>
                <div className="drive-import-file-actions">
                  <Button
                    onClick={() => importToRag(f.id, f.name)}
                    disabled={importing[f.id]}
                    variant="primary"
                    className={`admin-btn-compact${importing[f.id] ? ' loading' : ''}`}
                    aria-label={importing[f.id] ? `Importing ${f.name}` : `Import ${f.name} to knowledge base`}
                  >
                    {importing[f.id] ? 'Importing…' : 'Import'}
                  </Button>
                  {f.webViewLink && (
                    <a
                      href={f.webViewLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="drive-import-view-link"
                    >
                      View
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {files.length > initialLoadCount && (
            <div className="drive-import-more-wrap">
              <Button
                onClick={() => setShowAll(!showAll)}
                variant="secondary"
                className="admin-btn-compact"
              >
                {showAll ? 'Show less' : `Show ${files.length - initialLoadCount} more`}
              </Button>
            </div>
          )}

          {hasMore && showAll && (
            <div className="drive-import-more-wrap">
              <Button
                onClick={() => searchDrive(true)}
                disabled={loading}
                variant="secondary"
                className="admin-btn-compact"
              >
                {loading ? 'Loading…' : 'Load more files'}
              </Button>
            </div>
          )}
        </div>
      )}

      <DuplicateImportModal
        isOpen={showDuplicateModal}
        onClose={handleDuplicateCancel}
        fileName={duplicateFileInfo?.fileName}
        existingFile={duplicateFileInfo?.existingFile}
        onConfirm={handleDuplicateConfirm}
      />
    </div>
  )
}
